import { CustomPage, CustomRouteWithPage } from '@/types/pages';

import Figure from '@/components/serializers/Figure';
import Link from 'next/link';
import prepInternalUrl from '@/lib/prepInternalUrl';
import globalStyles from './styles.module.css';
import { imageUrlBuilder } from './sanityImage';

// The builder lives in sanityImage.ts so test-importable code can share the single
// instance; re-exported here because most of the app already imports it from this module.
export { imageUrlBuilder };

/**
 * Converts a string to a URL-friendly slug format
 * @param str - The string to be converted to a slug
 * @returns A slugified version of the input string
 */
export const slug = (str: string) =>
  str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

/**
 * Generates a background image URL for CSS usage
 * @param bgImage - Image object or string URL
 * @returns A CSS-ready background image URL or 'none'
 */
export const getBgImageUrl = (bgImage: Record<string, unknown> | string | undefined): string => {
  if (!bgImage) return 'none';
  if (typeof bgImage === 'string') return `url(${bgImage})`;
  const url = bgImage.hardCoded
    ? bgImage.hardCoded
    : imageUrlBuilder.image(bgImage.asset).width(1920).auto('format').quality(80).url();
  return `url(${url})`;
};

export const portableTextSerializer = {
  types: {
    figure: (props) => {
      return <Figure {...props.node} />;
    },
  },
  marks: {
    link: ({ mark, children }) => {
      const { href, blank } = mark;
      /* istanbul ignore if */
      if (typeof href !== 'string' || !href.trim()) {
        return <span>{children}</span>;
      }

      const isExternalLink = /(http(s?)):\/\//i.test(href) || href.includes('mailto:') || href.includes('tel:');
      return isExternalLink ? (
        <a href={href} target={blank ? '_blank' : '_self'} rel="noopener noreferrer">
          {children}
        </a>
      ) : (
        <Link href={prepInternalUrl(href)} target={blank ? '_blank' : '_self'}>
          {children}
        </Link>
      );
    },

    italic: function italic({ children }) {
      return children;
    },

    linkCta: (props) => {
      const href = props?.mark?.href;
      if (typeof href !== 'string' || !href.trim()) {
        return <span className={globalStyles.underline}>{props.children}</span>;
      }
      return (
        <span className={globalStyles.underline}>
          <Link href={prepInternalUrl(href)}>{props.children[0]}</Link>
        </span>
      );
    },

    internalLink: ({ mark, children }) => {
      const { href, blank } = mark;
      if (typeof href !== 'string' || !href.trim()) {
        return <span>{children}</span>;
      }
      return (
        <Link href={prepInternalUrl(href)} target={blank ? '_blank' : '_self'}>
          {children}
        </Link>
      );
    },

    cta: (props) => {
      const route = props?.mark?.route;
      if (typeof route !== 'string' || !route.trim()) {
        return <span>{props.children}</span>;
      }
      return <Link href={prepInternalUrl(route)}>{props.children[0]}</Link>;
    },
  },
};

export const groupTags = (tags) =>
  tags
    .flatMap((tag) => tag)
    .reduce((init, item) => {
      const itemSlug = slug(item);
      const index = init.findIndex((i) => i.slug === itemSlug);
      if (index !== -1) {
        init[index] = {
          ...init[index],
          count: init[index].count + 1,
        };
      } else {
        init = [
          ...init,
          {
            slug: itemSlug,
            label: item,
            count: 1,
          },
        ];
      }
      return init;
    }, []);

export const getDraftOrPageContent = (
  routesArray: CustomRouteWithPage[],
): { content: CustomRouteWithPage; isDraft: boolean } => {
  if (!routesArray || routesArray.length === 0) {
    return null;
  }

  const routeDraftIndex = routesArray.findIndex((page) => page.route._id.startsWith('drafts.'));
  const route = routeDraftIndex === -1 ? routesArray[0] : routesArray[routeDraftIndex];
  const pages = route.page as CustomPage[];

  if (!pages || pages.length === 0) {
    return {
      isDraft: routeDraftIndex !== -1,
      content: route,
    };
  }

  const pageDraftIndex = pages.findIndex((page) => page._id.startsWith('drafts.'));
  return {
    isDraft: routeDraftIndex !== -1 || pageDraftIndex !== -1,
    content: {
      ...route,
      page: pageDraftIndex === -1 ? pages[0] : pages[pageDraftIndex],
    },
  };
};
